import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import PageHead from "@/components/site/PageHead";
import { useLanguage, withLocalePrefix } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export default function DynamicPage() {
    const { slug } = useParams();
    const { locale } = useLanguage();
    const location = useLocation();
    const preview = new URLSearchParams(location.search).get("preview") === "1";
    const [state, setState] = useState({ status: "loading", data: null, seo: {} });

    useEffect(() => {
        window.scrollTo(0, 0);
        let alive = true;
        setState({ status: "loading", data: null, seo: {} });
        axios
            .get(`${API}/custom-pages/${slug}?lang=${locale}${preview ? "&preview=1" : ""}`, {
                withCredentials: preview,
            })
            .then((r) => {
                if (alive) setState({ status: "ready", data: r.data.data || {}, seo: r.data.seo || {} });
            })
            .catch(() => {
                if (alive) setState({ status: "notfound", data: null, seo: {} });
            });
        return () => {
            alive = false;
        };
    }, [slug, locale, preview]);

    if (state.status === "loading") {
        return (
            <main className="min-h-screen bg-[#FDFBF7] text-[#1E3A5F] flex items-center justify-center" data-testid="dynamic-page-loading">
                <Loader2 size={22} className="animate-spin text-[#64748B]" />
            </main>
        );
    }

    if (state.status === "notfound") {
        return (
            <main className="min-h-screen bg-[#FDFBF7] text-[#1E3A5F]" data-testid="dynamic-page-notfound">
                <Navbar />
                <div className="max-w-[900px] mx-auto px-6 md:px-12 pt-48 pb-32 text-center">
                    <div className="overline text-[#DC2626]">Erreur 404</div>
                    <h1 className="font-serif text-4xl md:text-5xl mt-4">Page introuvable.</h1>
                    <p className="mt-4 text-[#64748B]">Cette page n’existe pas ou n’est pas encore publiée.</p>
                    <Link to={withLocalePrefix("/", locale)} className="btn-primary mt-10 inline-flex" data-testid="notfound-home-link">
                        <ArrowLeft size={16} strokeWidth={1.5} /> Retour à l’accueil
                    </Link>
                </div>
                <Footer />
            </main>
        );
    }

    const d = state.data || {};
    const hasHero = Boolean(d.hero_image);

    return (
        <main data-testid="dynamic-page" className="min-h-screen bg-[#FDFBF7] text-[#1E3A5F]">
            <PageHead seo={state.seo} path={`/${slug}`} />
            <Navbar />

            {hasHero ? (
                <section className="relative min-h-[62vh] flex items-end overflow-hidden pt-32">
                    <motion.img
                        src={d.hero_image}
                        alt={d.title || ""}
                        initial={{ scale: 1.06, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 1.6, ease }}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A5F]/40 via-[#1E3A5F]/25 to-[#1E3A5F]/70" />
                    <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pb-20 md:pb-28 w-full">
                        <motion.h1
                            initial={{ opacity: 0, y: 28 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.1, delay: 0.35, ease }}
                            data-testid="dynamic-page-title"
                            className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl"
                        >
                            {d.title}
                        </motion.h1>
                    </div>
                </section>
            ) : (
                <section className="pt-44 pb-8">
                    <div className="max-w-[1100px] mx-auto px-6 md:px-12">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#DC2626]" />
                            <span className="overline">L’Atelier du meuble</span>
                        </div>
                        <h1 data-testid="dynamic-page-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
                            {d.title}
                        </h1>
                    </div>
                </section>
            )}

            <section className="py-20 md:py-28">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                    {d.body && (
                        <div className={d.image ? "md:col-span-7" : "md:col-span-9"}>
                            <p data-testid="dynamic-page-body" className="text-lg md:text-xl leading-relaxed text-[#334155] whitespace-pre-wrap">
                                {d.body}
                            </p>
                        </div>
                    )}
                    {d.image && (
                        <div className={d.body ? "md:col-span-5" : "md:col-span-12"}>
                            <div className="img-zoom aspect-[4/5] bg-[#F4F2EE]">
                                <img src={d.image} alt={d.title || ""} loading="lazy" data-testid="dynamic-page-image" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </main>
    );
}
